import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import LandingPage from "@/pages/LandingPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CookiePolicy from "@/pages/CookiePolicy";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import VendorDashboard from "@/pages/vendor/VendorDashboard";
import VendorPitchesPage from "@/pages/vendor/VendorPitchesPage";
import NewPitchPage from "@/pages/vendor/NewPitchPage";
import VendorPitchDetailPage from "@/pages/vendor/VendorPitchDetailPage";
import MerchantDashboard from "@/pages/merchant/MerchantDashboard";
import MerchantPitchesPage from "@/pages/merchant/MerchantPitchesPage";
import AddPitchPage from "@/pages/merchant/AddPitchPage";
import MerchantPitchDetailPage from "@/pages/merchant/MerchantPitchDetailPage";
import MerchantRecommendationsPage from "@/pages/merchant/MerchantRecommendationsPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPitchesPage from "@/pages/admin/AdminPitchesPage";
import AdminPitchReviewPage from "@/pages/admin/AdminPitchReviewPage";
import AdminAnalyticsPage from "@/pages/admin/AdminAnalyticsPage";

const PASS_THROUGH_ROUTES = new Set(["/", "/privacy", "/terms", "/cookies"]);

const ApiButtonNoopGuard = () => {
  const location = useLocation();

  useEffect(() => {
    const shouldBlock = !PASS_THROUGH_ROUTES.has(location.pathname);
    if (!shouldBlock) return undefined;

    const handleClickCapture = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const clickable = target.closest(
        "button, [role='button'], input[type='button'], input[type='submit']"
      );
      if (!clickable) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    document.addEventListener("click", handleClickCapture, true);
    return () => document.removeEventListener("click", handleClickCapture, true);
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ApiButtonNoopGuard />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/vendor"
            element={
              <ProtectedRoute allowedRoles={["vendor"]}>
                <VendorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/pitches"
            element={
              <ProtectedRoute allowedRoles={["vendor"]}>
                <VendorPitchesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/pitches/new"
            element={
              <ProtectedRoute allowedRoles={["vendor"]}>
                <NewPitchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/pitches/:pitchId"
            element={
              <ProtectedRoute allowedRoles={["vendor"]}>
                <VendorPitchDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchant"
            element={
              <ProtectedRoute allowedRoles={["merchant"]}>
                <MerchantDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchant/pitches"
            element={
              <ProtectedRoute allowedRoles={["merchant"]}>
                <MerchantPitchesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchant/pitches/add"
            element={
              <ProtectedRoute allowedRoles={["merchant"]}>
                <AddPitchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchant/pitches/:pitchId"
            element={
              <ProtectedRoute allowedRoles={["merchant"]}>
                <MerchantPitchDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchant/recommendations"
            element={
              <ProtectedRoute allowedRoles={["merchant"]}>
                <MerchantRecommendationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pitches"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminPitchesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pitches/:pitchId"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminPitchReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
